import React, { useEffect, useState, useContext } from "react";
import Button from "react-bootstrap/Button";
import Table from "react-bootstrap/Table";
import Dropdown from "react-bootstrap/Dropdown";
import { Link } from "react-router-dom";
import { useAPIPost, usePost } from "./API";
import { useParams } from "react-router-dom";
import { snakeToEnglish, copyToClipboard } from "./Util";
import { isAidKitDocumentsUrl } from "./utils/signedS3Urls";
import { useLocalizedStrings } from "./Localization";
import { ConfigurationContext, SupportedLanguage } from "./Context";
import { Alert } from "react-bootstrap";
import { AddNew } from "./Dashboard/AddNewApplicant";
import { useToast } from "@aidkitorg/component-library";
import { URLViewer } from './Applicant'
import DLOfflineButton from "./Components/DLOfflineButton";

type ActionHandler = (params: {}) => Promise<{ status: string; message?: string } | undefined>;
type ActionButtonAction = { path: string } | { handler: ActionHandler };
type ActionButtonProps = ActionButtonAction & { title: string, variant?: string, onSuccess?: any };
function ActionButton(props: ActionButtonProps) {
  const L = useLocalizedStrings();
  const pathAction = useAPIPost('path' in props ? props.path : '');
  const action: ActionHandler = 'handler' in props ? props.handler : pathAction;
  const [result, setResult] = useState("");

  async function doAction() {
    const response = await action({});
    if (response && response.status === "ok") {
      setResult(response.message || L.dashboard.done);
      if (props.onSuccess) props.onSuccess();
    } else {
      setResult(L.dashboard.error);
    }
  }

  if (result) {
    return <b>{result}</b>;
  }

  if (props.variant && props.variant === "dropdown_link") {
    return <Dropdown.Item onClick={doAction}>{props.title}</Dropdown.Item>;
  }

  return (
    <Button onClick={doAction} variant="primary">
      {props.title}
    </Button>
  );
}

type Applicant = {
  uid: string,
  hidden: boolean | null
  [infoKey: string]: any
};

function Dashboard(props: { path?: string, customId?: string }) {
  const [showAdd, setShowAdd] = useState(false);
  let { path, customId } = useParams() as Record<string, string | undefined>;
  if (props.customId) customId = props.customId;
  const fetchDashboard = usePost("/applicants/custom");
  const [ dashboard, setDashboard ] = useState<any>();
  const L = useLocalizedStrings();
  const configuration = useContext(ConfigurationContext);
  const { toast } = useToast();
  const hideApplicant = usePost("/applicant/hide");
  const unhideApplicant = usePost("/applicant/unhide");

  const dashboardId = customId || path;
  async function makeDashboardRequest(){
    const response = await fetchDashboard({
      id: dashboardId!
    });
    if (!(response as any).error) {
      setDashboard(response);
    }
  }

  useEffect(
    () => {
      makeDashboardRequest();
    },
    [dashboardId]
  );

  const getSignedURL = usePost('/v2/document/view');
  const [signedURLs, setSignedURLs] = useState<Record<string, string[]>>({});
  const mine = props.path?.match("screened_by_me") || (customId && customId.match("screened_by_me"));
  const myOrg = path?.match("my_organization") || (customId && customId.match("my_organization"));

  const [sortedApplicants, setSortedApplicants] = useState([] as Applicant[]);
  const [sortKey, setSortKey] = useState("name");

  function sortString(a: any, b: any) {
    if (a === b) return 0;
    if (!a) return -1;
    if (!b) return 1;
    if (a < b) return -1;
    if (a > b) return 1;
  }

  function sortHidden(a: Applicant, b: Applicant) {
    if (a.hidden === b.hidden) return 0;
    if (a.hidden) return 1;
    if (b.hidden) return -1;
    return 0;
  }

  function sortIneligible(a: Applicant, b: Applicant) {
    if (a.stage === b.stage) return 0;
    if (['Ineligible', 'Opted Out', 'Unreachable'].indexOf(a.stage) !== -1) return 1;
    if (['Ineligible', 'Opted Out', 'Unreachable'].indexOf(b.stage) !== -1) return -1;
    return 0;
  }

  useEffect(() => {
    (async () => {
      let urls: string[] = [];
      (dashboard?.data || []).map((row: any) => {
        for (const [key, value] of Object.entries(row) as [string, string][]) {
          if (isAidKitDocumentsUrl(value)) {
            urls.push(...value.split(','));
          }
        }
      })

      if (urls.length) {
        const { paths } = await getSignedURL({
          paths: urls,
        });
        setSignedURLs(paths);
      }
    })();
  }, [dashboard?.data])

  useEffect(() => {
    if (dashboard?.data) {
      setSortedApplicants(
        (dashboard?.data || [])
          .sort((a: any, b: any) => {
            return sortString(a[sortKey], b[sortKey]);
          })
          .sort((a: Applicant, b: Applicant) => {
            return sortIneligible(a, b);
          })
          .sort((a: Applicant, b: Applicant) => {
            return sortHidden(a, b);
          })
          .slice(0)
      );
    }
  }, [sortKey, dashboard?.data]);

  if (!configuration?.user) {
    return (
      <div className="p-5 d-flex flex-column justify-content-center" style={{ height: "500px" }}>
      </div>
    )
  }

  if (!dashboard?.data) {
    return null;
  }

  return (
    <div className="p-5">
      <h3>
        {customId && dashboard.title && !mine && customId !== 'all' ? (L.dashboard as any)[dashboard.title] ?? dashboard.title : <></>}
        {!customId && dashboard.title ? (L.dashboard as any)[dashboard.title] ?? dashboard.title : <></>}
        {((dashboard.title && (mine || customId === 'all')) || !dashboard.title) && (
          <>
            {myOrg && (
              <>
                {L.dashboard.org_applicants}
              </>
            )}
            {mine ? L.dashboard.my_applicants : (!myOrg ? L.dashboard.all_applicants : '')}{" "}
            {configuration.paused !== 'true' &&
              <>
                {(!dashboard['queues'] || (dashboard['queues'].indexOf('any') !== -1)) && (
                  <>
                    <Button variant="primary" onClick={() => setShowAdd(true)}>
                      {L.dashboard.add_new_applicant}
                    </Button>
                    &nbsp;
                    <DLOfflineButton applicants={sortedApplicants} />
                    &nbsp;
                  </>
                )}
              </>}
          </>
        )}
      </h3>
      {myOrg && (
        <>
          <h6>{L.applicant.organization}: {configuration.organization?.name || ''}</h6>
        </>
      )}
      {myOrg && configuration.use_quotas === 'true' && (
        <>
          <h6>{L.dashboard.your_org_screened} {configuration.org_quota_usage} {L.dashboard.of_the} {configuration.quota} {L.dashboard.applicants_in_your_org_quota}</h6>
        </>
      )}
      {mine && (<>
        {configuration.use_quotas === 'true' &&
          <>
            <h6>{L.dashboard.your_org_screened} {configuration.org_quota_usage} {L.dashboard.of_the} {configuration.quota} {L.dashboard.applicants_in_your_org_quota}</h6>
            {(configuration.use_screener_quotas || 'undefined') !== 'true' && <h6>{L.dashboard.you_have_screened} {sortedApplicants.length}.</h6>}
          </>}
        {configuration.use_screener_quotas && configuration.use_screener_quotas === 'true' &&
          <h6>{L.dashboard.you_have_screened} {sortedApplicants.length} {L.dashboard.of_the} {configuration.screener_quota} {L.dashboard.applicants_in_your_screener_quota}</h6>}
      </>
      )}
      {dashboard.stats &&
        Object.keys(dashboard.stats).map((s) => {
          return (
            <div>
              <b>{s}: </b>
              {dashboard.stats[s]}
            </div>
          );
        })}
      <br />
      {sortedApplicants.length === 0 && <Alert variant="info">{L.dashboard.no_applicants_here}</Alert>}
      {sortedApplicants.length > 0 && (
        <Table bordered>
          <thead>
            <tr>
              {dashboard.fields &&
                dashboard.fields.map((f: string, idx: number) => {
                  return (
                    <th key={'header-' + f + '-' + idx.toString()}>
                      <Button variant="link" onClick={(e) => setSortKey(f)}>
                        {snakeToEnglish(f)}
                      </Button>
                    </th>
                  );
                })}
            </tr>
          </thead>
          <tbody id={'applicants-table'}>
            {sortedApplicants.map((a: Applicant) => {
              if (a.hidden && a.hidden === true && !configuration.roles?.includes('admin')) return null;

              return (
                <tr key={a.uid} className={a.hidden ? 'color-hidden' : ''}>
                  {dashboard.fields &&
                    dashboard.fields.map((f: string, idx: number) => {
                      if (f === "legal_name") {
                        return (
                          <td key={a.uid + '-' + f + '-' + idx.toString()} style={{ justifyContent: 'flex-start' }}>
                            <DLOfflineButton applicants={[a]} />
                            &nbsp;
                            <Link to={"/applicant/" + a.uid}>{a[f] || '(unnamed)'}
                              {a.hidden && <>&nbsp;{L.dashboard.hidden}</>}
                            </Link>
                            {configuration.roles?.includes('admin') && (
                              <Dropdown>
                                <Dropdown.Toggle variant="link">
                                  ...
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  {!a.hidden && (
                                    <ActionButton key={'hide-' + a.uid} title={"Hide"}
                                      variant="dropdown_link"
                                      handler={() => hideApplicant({ uid: a.uid })}
                                      onSuccess={makeDashboardRequest}
                                    />
                                  )}
                                  {a.hidden && (
                                    <ActionButton key={'unhide-' + a.uid} title={"Unhide"}
                                      variant="dropdown_link"
                                      handler={() => unhideApplicant({ uid: a.uid })}
                                      onSuccess={makeDashboardRequest}
                                    />
                                  )}
                                  <Dropdown.Item onClick={() => { copyToClipboard(a.uid); toast({description: L.copied_to_clipboard, variant: 'success'}) }}>{L.dashboard.copy_uid}</Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            )}
                          </td>
                        );
                      }
                      if (a[f] && a[f][0] === "/") {
                        return <ActionButton key={f} title={f} path={a[f]} />;
                      }
                      if (isAidKitDocumentsUrl(a[f])) {
                        return <URLViewer urls={(a[f] || '').split(',').filter((a: any) => a)} signedURLs={signedURLs} Viewer={"screener"} />;
                      }
                      return <td key={a.uid + '-' + f + '-' + idx.toString()}>{a[f]}</td>;
                    })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
      <AddNew show={showAdd} setShow={setShowAdd} />
    </div>
  );
}

export default Dashboard;
